import { useEffect, useState, useCallback, useRef } from 'react'
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
} from 'recharts'
import { RefreshCw, CheckCircle, XCircle, AlertTriangle, Activity } from 'lucide-react'
import toast from 'react-hot-toast'
import { monitoringService } from '../../../services/monitoringService'
import type {
  MonitoringOverview, ServiceStatus, APIPerformanceResponse,
  ErrorListResponse, SystemResources, EndpointStat, ErrorEvent,
} from '../../../types/api.types'

// ── Helpers ──

function serviceColor(s: ServiceStatus): string {
  if (!s.is_healthy) return '#dc2626'
  if (s.latency_ms != null && s.latency_ms > 500) return '#d97706'
  return '#16a34a'
}

function latencyColor(ms: number): string {
  if (ms < 500) return 'text-green-600'
  if (ms < 1500) return 'text-amber-600'
  return 'text-red-600'
}

function resourceColor(pct: number): string {
  if (pct < 70) return 'bg-green-500'
  if (pct < 85) return 'bg-amber-400'
  return 'bg-red-500'
}

function resourceTextColor(pct: number): string {
  if (pct < 70) return 'text-green-700'
  if (pct < 85) return 'text-amber-700'
  return 'text-red-700'
}

function severityBadge(s: string): string {
  const map: Record<string, string> = {
    CRITICAL: 'bg-red-100 text-red-800',
    ERROR:    'bg-orange-100 text-orange-800',
    WARNING:  'bg-amber-100 text-amber-800',
  }
  return map[s] ?? 'bg-slate-100 text-slate-500'
}

function fmtUptime(seconds: number | null): string {
  if (!seconds) return '—'
  const d = Math.floor(seconds / 86400)
  const h = Math.floor((seconds % 86400) / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  if (d > 0) return `${d}d ${h}h`
  if (h > 0) return `${h}h ${m}m`
  return `${m}m`
}

function fmtMem(mb: number): string {
  return mb >= 1024 ? `${(mb / 1024).toFixed(1)} GB` : `${mb.toFixed(0)} MB`
}

function fmtTime(ts: string): string {
  return new Date(ts).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' })
}

function fmtDatetime(ts: string): string {
  return new Date(ts).toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  })
}

// ── Sub-components ──

function KpiPill({
  label, value, alert,
}: { label: string; value: string | number; alert?: boolean }): JSX.Element {
  return (
    <div className={`flex flex-col items-center rounded-xl border px-5 py-3.5 ${alert ? 'border-red-200 bg-red-50' : 'border-gray-200 bg-white'}`}>
      <span className={`text-[24px] font-bold leading-tight ${alert ? 'text-red-700' : 'text-gray-900'}`}>{value}</span>
      <span className={`mt-0.5 text-[11px] font-medium ${alert ? 'text-red-500' : 'text-gray-500'}`}>{label}</span>
    </div>
  )
}

function GaugeBar({ label, pct, left, right }: { label: string; pct: number; left: string; right: string }): JSX.Element {
  return (
    <div>
      <div className="mb-1 flex items-center justify-between">
        <span className="text-[12px] font-medium text-gray-700">{label}</span>
        <span className={`text-[13px] font-bold ${resourceTextColor(pct)}`}>{pct.toFixed(0)}%</span>
      </div>
      <div className="h-2.5 w-full overflow-hidden rounded-full bg-gray-100">
        <div
          className={`h-full rounded-full transition-all ${resourceColor(pct)}`}
          style={{ width: `${Math.min(pct, 100)}%` }}
        />
      </div>
      <div className="mt-0.5 flex justify-between text-[10px] text-gray-400">
        <span>{left}</span>
        <span>{right}</span>
      </div>
    </div>
  )
}

function EndpointTable({ rows, title, highlightCol, highlightKey }: {
  rows: EndpointStat[]
  title: string
  highlightCol: string
  highlightKey: keyof EndpointStat
}): JSX.Element {
  return (
    <div className="min-w-0">
      <p className="mb-2 text-[13px] font-semibold text-gray-800">{title}</p>
      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="w-full min-w-[420px] table-fixed">
          <colgroup>
            <col className="w-full" />
            <col className="w-20" />
            <col className="w-20" />
            <col className="w-14" />
            <col className="w-20" />
          </colgroup>
          <thead>
            <tr className="bg-gray-50 text-left">
              <th className="px-3 py-2 text-[11px] font-medium uppercase tracking-wide text-gray-500">Endpoint</th>
              <th className="px-3 py-2 text-[11px] font-medium uppercase tracking-wide text-gray-500">Method</th>
              <th className="px-3 py-2 text-right text-[11px] font-medium uppercase tracking-wide text-gray-500">Requests</th>
              <th className="px-3 py-2 text-right text-[11px] font-medium uppercase tracking-wide text-gray-500">Errors</th>
              <th className="px-3 py-2 text-right text-[11px] font-medium uppercase tracking-wide text-gray-500">{highlightCol}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-t border-gray-100 hover:bg-gray-50/40">
                <td className="px-3 py-2">
                  <span className="block truncate font-mono text-[11px] text-gray-600">{r.normalized_path}</span>
                </td>
                <td className="px-3 py-2">
                  <span className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-[10px] text-gray-600">{r.method}</span>
                </td>
                <td className="px-3 py-2 text-right text-[12px] text-gray-700">{r.total_requests.toLocaleString()}</td>
                <td className="px-3 py-2 text-right text-[12px]">
                  {r.error_count > 0
                    ? <span className="font-medium text-red-600">{r.error_count}</span>
                    : <span className="text-gray-400">—</span>}
                </td>
                <td className={`px-3 py-2 text-right text-[12px] font-semibold ${latencyColor(Number(r[highlightKey]))}`}>
                  {Number(r[highlightKey]).toFixed(0)}ms
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ── Main page ──

type WindowHours = 1 | 6 | 24
const WINDOWS: { label: string; value: WindowHours }[] = [
  { label: '1h', value: 1 },
  { label: '6h', value: 6 },
  { label: '24h', value: 24 },
]

export function PlatformMonitoringPage(): JSX.Element {
  const [overview, setOverview] = useState<MonitoringOverview | null>(null)
  const [services, setServices] = useState<ServiceStatus[]>([])
  const [performance, setPerformance] = useState<APIPerformanceResponse | null>(null)
  const [system, setSystem] = useState<SystemResources | null>(null)
  const [errorLog, setErrorLog] = useState<ErrorListResponse | null>(null)

  const [overviewLoading, setOverviewLoading] = useState(false)
  const [servicesLoading, setServicesLoading] = useState(false)
  const [perfLoading, setPerfLoading] = useState(false)
  const [systemLoading, setSystemLoading] = useState(false)
  const [errLoading, setErrLoading] = useState(false)

  const [windowHours, setWindowHours] = useState<WindowHours>(1)
  const [errSource, setErrSource] = useState<'' | 'backend' | 'frontend'>('')
  const [errSeverity, setErrSeverity] = useState<'' | 'ERROR' | 'WARNING' | 'CRITICAL'>('')
  const [errUnresolved, setErrUnresolved] = useState(false)

  const [resolvingId, setResolvingId] = useState<number | null>(null)

  const overviewRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const systemRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const errRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const servicesRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // ── Loaders ──

  const loadOverview = useCallback(async () => {
    setOverviewLoading(true)
    try {
      const d = await monitoringService.getOverview()
      setOverview(d)
      setServices(d.services)
    } catch { /* non-critical */ }
    finally { setOverviewLoading(false) }
  }, [])

  const loadServices = useCallback(async () => {
    setServicesLoading(true)
    try { setServices(await monitoringService.getServices()) }
    catch { /* non-critical */ }
    finally { setServicesLoading(false) }
  }, [])

  const loadPerformance = useCallback(async (w: WindowHours) => {
    setPerfLoading(true)
    try { setPerformance(await monitoringService.getPerformance(w)) }
    catch { setPerformance(null) }
    finally { setPerfLoading(false) }
  }, [])

  const loadSystem = useCallback(async () => {
    setSystemLoading(true)
    try { setSystem(await monitoringService.getSystem()) }
    catch { setSystem(null) }
    finally { setSystemLoading(false) }
  }, [])

  const loadErrors = useCallback(async (
    source: '' | 'backend' | 'frontend',
    severity: '' | 'ERROR' | 'WARNING' | 'CRITICAL',
    unresolvedOnly: boolean,
  ) => {
    setErrLoading(true)
    try {
      const d = await monitoringService.getErrors({
        source: source || undefined,
        severity: severity || undefined,
        is_resolved: unresolvedOnly ? false : undefined,
        limit: 100,
      })
      setErrorLog(d)
    } catch { setErrorLog(null) }
    finally { setErrLoading(false) }
  }, [])

  // ── Initial loads + intervals ──

  useEffect(() => {
    loadOverview()
    loadSystem()
    loadPerformance(windowHours)
    loadErrors(errSource, errSeverity, errUnresolved)

    overviewRef.current = setInterval(loadOverview, 60_000)
    systemRef.current = setInterval(loadSystem, 30_000)
    errRef.current = setInterval(() => loadErrors(errSource, errSeverity, errUnresolved), 60_000)
    servicesRef.current = setInterval(loadServices, 30_000)

    return () => {
      if (overviewRef.current) clearInterval(overviewRef.current)
      if (systemRef.current) clearInterval(systemRef.current)
      if (errRef.current) clearInterval(errRef.current)
      if (servicesRef.current) clearInterval(servicesRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => { loadPerformance(windowHours) }, [windowHours, loadPerformance])
  useEffect(() => { loadErrors(errSource, errSeverity, errUnresolved) }, [errSource, errSeverity, errUnresolved, loadErrors])

  async function resolveError(e: ErrorEvent) {
    setResolvingId(e.id)
    try {
      await monitoringService.resolveError(e.id)
      setErrorLog((prev) => prev
        ? { ...prev, errors: prev.errors.filter((x) => x.id !== e.id), unresolved: Math.max(0, prev.unresolved - 1) }
        : prev
      )
      toast.success('Error marked as resolved.')
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Failed to resolve')
    } finally {
      setResolvingId(null)
    }
  }

  function refreshAll() {
    loadOverview()
    loadServices()
    loadSystem()
    loadPerformance(windowHours)
    loadErrors(errSource, errSeverity, errUnresolved)
  }

  // Derived
  const chartData = (performance?.timeline ?? []).map((b) => ({
    name: b.label, total: b.total, errors: b.errors, ok: b.total - b.errors,
  }))

  const unhealthyServices = services.filter((s) => !s.is_healthy)
  const resourceAlert = system && (system.cpu_percent > 85 || system.ram_percent > 90 || system.disk_percent > 90)

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900 sm:text-2xl">Monitoring</h1>
          <p className="mt-1 text-sm text-gray-500">Platform health, performance, and error tracking</p>
        </div>
        <button
          type="button"
          onClick={refreshAll}
          disabled={overviewLoading}
          className="flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-2 text-[13px] text-gray-600 hover:bg-gray-50 disabled:opacity-50"
        >
          <RefreshCw size={13} className={overviewLoading ? 'animate-spin' : ''} />
          Refresh all
        </button>
      </div>

      {/* Alerts */}
      {unhealthyServices.length > 0 && (
        <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-2.5 text-[13px] text-red-700">
          <XCircle size={15} />
          <span><strong>{unhealthyServices.length}</strong> service{unhealthyServices.length !== 1 ? 's' : ''} unhealthy: {unhealthyServices.map((s) => s.name).join(', ')}</span>
        </div>
      )}
      {resourceAlert && (
        <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-[13px] text-amber-700">
          <AlertTriangle size={15} />
          <span>System resource usage is high — check CPU / RAM / Disk gauges below.</span>
        </div>
      )}

      {/* KPI row */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
        <KpiPill label="Requests / hr"    value={overview?.requests_last_hour ?? '—'} />
        <KpiPill label="Errors / hr"      value={overview?.errors_last_hour ?? '—'}    alert={(overview?.errors_last_hour ?? 0) > 0} />
        <KpiPill label="Error Rate"       value={overview ? `${overview.error_rate_pct.toFixed(1)}%` : '—'} alert={(overview?.error_rate_pct ?? 0) > 1} />
        <KpiPill label="Avg Response"     value={overview ? `${overview.avg_response_ms.toFixed(0)}ms` : '—'} alert={(overview?.avg_response_ms ?? 0) > 2000} />
        <KpiPill label="Unresolved Errors" value={overview?.unresolved_errors ?? '—'} alert={(overview?.unresolved_errors ?? 0) > 0} />
      </div>

      {/* Services + System Resources row */}
      <div className="grid gap-5 lg:grid-cols-2">
        {/* Services */}
        <div className="rounded-xl border border-gray-200 bg-white">
          <div className="flex items-center justify-between border-b border-gray-100 px-5 py-3.5">
            <div className="flex items-center gap-2">
              <Activity size={15} className="text-brand-green" />
              <h2 className="text-[15px] font-bold text-gray-900">Services</h2>
            </div>
            <button
              type="button"
              onClick={loadServices}
              disabled={servicesLoading}
              className="flex items-center gap-1 text-[11px] text-gray-400 hover:text-gray-600 disabled:opacity-40"
            >
              <RefreshCw size={11} className={servicesLoading ? 'animate-spin' : ''} />
              Refresh
            </button>
          </div>
          <div className="divide-y divide-gray-100">
            {services.length === 0 ? (
              <p className="px-5 py-8 text-center text-[13px] text-gray-400">Loading services...</p>
            ) : services.map((s) => (
              <div key={s.name} className="flex items-center justify-between px-5 py-3">
                <div className="flex items-center gap-2.5">
                  <span className="h-2.5 w-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: serviceColor(s) }} />
                  <span className="text-[13px] font-medium text-gray-900">{s.name}</span>
                </div>
                <div className="flex items-center gap-4 text-right">
                  <span className="text-[12px] text-gray-500">
                    {s.latency_ms != null ? `${s.latency_ms}ms` : '—'}
                  </span>
                  <span className={`text-[12px] ${s.is_healthy ? 'text-gray-600' : 'font-medium text-red-600'}`}>
                    {s.status_text}
                  </span>
                  <span className="text-[11px] text-gray-400">{fmtTime(s.checked_at)}</span>
                  {s.is_healthy
                    ? <CheckCircle size={14} className="text-green-500" />
                    : <XCircle size={14} className="text-red-500" />}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* System Resources */}
        <div className="rounded-xl border border-gray-200 bg-white">
          <div className="flex items-center justify-between border-b border-gray-100 px-5 py-3.5">
            <h2 className="text-[15px] font-bold text-gray-900">System Resources</h2>
            {system && (
              <span className="text-[12px] text-gray-400">Uptime {fmtUptime(system.uptime_seconds)}</span>
            )}
          </div>
          <div className="space-y-4 p-5">
            {systemLoading && !system ? (
              <p className="py-8 text-center text-[13px] text-gray-400">Loading...</p>
            ) : system ? (
              <>
                <GaugeBar
                  label="CPU"
                  pct={system.cpu_percent}
                  left={`${system.cpu_percent.toFixed(0)}% used`}
                  right=""
                />
                <GaugeBar
                  label="RAM"
                  pct={system.ram_percent}
                  left={`${fmtMem(system.ram_used_mb)} used`}
                  right={fmtMem(system.ram_total_mb)}
                />
                <GaugeBar
                  label="Disk"
                  pct={system.disk_percent}
                  left={`${system.disk_used_gb.toFixed(1)} GB used`}
                  right={`${system.disk_total_gb.toFixed(1)} GB total`}
                />
                {system.process_count != null && (
                  <p className="text-[12px] text-gray-500">Processes: <span className="font-medium text-gray-700">{system.process_count}</span></p>
                )}
              </>
            ) : (
              <p className="py-8 text-center text-[13px] text-gray-400">Unavailable</p>
            )}
          </div>
        </div>
      </div>

      {/* API Performance */}
      <div className="rounded-xl border border-gray-200 bg-white">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 px-5 py-3.5">
          <h2 className="text-[15px] font-bold text-gray-900">
            API Performance
            {performance && <span className="ml-2 text-[12px] font-normal text-gray-400">{performance.window_label}</span>}
          </h2>
          <div className="flex items-center gap-2">
            {WINDOWS.map((w) => (
              <button
                key={w.value}
                type="button"
                onClick={() => setWindowHours(w.value)}
                className={`rounded-lg px-3 py-1 text-[12px] font-medium transition-colors ${
                  windowHours === w.value
                    ? 'bg-brand-green text-black'
                    : 'border border-gray-200 text-gray-600 hover:bg-gray-50'
                }`}
              >
                {w.label}
              </button>
            ))}
            <button
              type="button"
              onClick={() => loadPerformance(windowHours)}
              disabled={perfLoading}
              className="flex items-center gap-1 text-[11px] text-gray-400 hover:text-gray-600 disabled:opacity-40"
            >
              <RefreshCw size={11} className={perfLoading ? 'animate-spin' : ''} />
            </button>
          </div>
        </div>

        <div className="p-5 space-y-5">
          {/* Summary pills */}
          {performance && (
            <div className="flex flex-wrap gap-3 text-[12px]">
              {[
                ['Requests', performance.total_requests.toLocaleString()],
                ['Errors', performance.total_errors.toLocaleString()],
                ['Error Rate', `${performance.error_rate_pct.toFixed(1)}%`],
                ['Avg Response', `${performance.avg_ms.toFixed(0)}ms`],
                ['Req/min', performance.requests_per_min.toFixed(1)],
              ].map(([label, val]) => (
                <span key={label} className="rounded-full border border-gray-200 bg-gray-50 px-3 py-1 text-gray-700">
                  <span className="font-semibold">{val}</span> <span className="text-gray-400">{label}</span>
                </span>
              ))}
            </div>
          )}

          {/* Timeline chart */}
          {perfLoading ? (
            <p className="py-8 text-center text-[13px] text-gray-400">Loading...</p>
          ) : chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={chartData} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#9ca3af' }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 10, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                <Tooltip
                  contentStyle={{ border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 12 }}
                  formatter={(val, name) => [Number(val).toLocaleString(), name === 'ok' ? 'OK' : 'Errors']}
                />
                <Bar dataKey="ok" fill="#a3c93a" radius={[3, 3, 0, 0]} stackId="a" />
                <Bar dataKey="errors" fill="#f87171" radius={[3, 3, 0, 0]} stackId="a" />
              </BarChart>
            </ResponsiveContainer>
          ) : null}

          {/* Endpoint tables */}
          {performance && performance.top_endpoints.length > 0 && (
            <div className="grid gap-5 lg:grid-cols-2">
              <EndpointTable
                rows={performance.top_endpoints.slice(0, 10)}
                title="Top Endpoints by Volume"
                highlightCol="Avg ms"
                highlightKey="avg_ms"
              />
              <EndpointTable
                rows={performance.slowest_endpoints.slice(0, 10)}
                title="Slowest Endpoints (p95)"
                highlightCol="p95 ms"
                highlightKey="p95_ms"
              />
            </div>
          )}
        </div>
      </div>

      {/* Error Log */}
      <div className="rounded-xl border border-gray-200 bg-white">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 px-5 py-3.5">
          <div className="flex items-center gap-2">
            <h2 className="text-[15px] font-bold text-gray-900">Error Log</h2>
            {errorLog && errorLog.unresolved > 0 && (
              <span className="rounded-full bg-red-100 px-2.5 py-0.5 text-[12px] font-semibold text-red-700">
                {errorLog.unresolved} unresolved
              </span>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={errSource}
              onChange={(e) => setErrSource(e.target.value as '' | 'backend' | 'frontend')}
              className="rounded-lg border border-gray-200 px-2.5 py-1.5 text-[12px] outline-none focus:border-brand-green"
            >
              <option value="">All Sources</option>
              <option value="backend">Backend</option>
              <option value="frontend">Frontend</option>
            </select>
            <select
              value={errSeverity}
              onChange={(e) => setErrSeverity(e.target.value as '' | 'ERROR' | 'WARNING' | 'CRITICAL')}
              className="rounded-lg border border-gray-200 px-2.5 py-1.5 text-[12px] outline-none focus:border-brand-green"
            >
              <option value="">All Severities</option>
              <option value="CRITICAL">Critical</option>
              <option value="ERROR">Error</option>
              <option value="WARNING">Warning</option>
            </select>
            <label className="flex cursor-pointer items-center gap-1.5 text-[12px] text-gray-600">
              <input
                type="checkbox"
                checked={errUnresolved}
                onChange={(e) => setErrUnresolved(e.target.checked)}
                className="h-3.5 w-3.5 rounded"
              />
              Unresolved only
            </label>
          </div>
        </div>

        {errLoading ? (
          <p className="py-12 text-center text-[13px] text-gray-400">Loading...</p>
        ) : !errorLog || errorLog.errors.length === 0 ? (
          <p className="py-12 text-center text-[13px] text-gray-400">No errors match the current filters.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px]">
              <thead>
                <tr className="bg-gray-50 text-left">
                  <th className="px-4 py-2.5 text-[11px] font-medium uppercase tracking-wide text-gray-500">Source</th>
                  <th className="px-4 py-2.5 text-[11px] font-medium uppercase tracking-wide text-gray-500">Severity</th>
                  <th className="px-4 py-2.5 text-[11px] font-medium uppercase tracking-wide text-gray-500">Endpoint</th>
                  <th className="px-4 py-2.5 text-[11px] font-medium uppercase tracking-wide text-gray-500">Message</th>
                  <th className="px-4 py-2.5 text-[11px] font-medium uppercase tracking-wide text-gray-500">Time</th>
                  <th className="px-4 py-2.5 text-[11px] font-medium uppercase tracking-wide text-gray-500">Action</th>
                </tr>
              </thead>
              <tbody>
                {errorLog.errors.map((e) => (
                  <tr key={e.id} className="border-t border-gray-100 hover:bg-gray-50/40">
                    <td className="px-4 py-3 text-[13px]">
                      {e.source === 'frontend' ? '🌐' : '⚙️'} <span className="text-gray-500">{e.source}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${severityBadge(e.severity)}`}>
                        {e.severity}
                      </span>
                    </td>
                    <td className="max-w-[160px] px-4 py-3">
                      <span className="block truncate font-mono text-[11px] text-gray-500">{e.endpoint ?? '—'}</span>
                    </td>
                    <td className="max-w-xs px-4 py-3">
                      <p className="line-clamp-2 text-[12px] text-gray-700">{e.message}</p>
                    </td>
                    <td className="px-4 py-3 text-[12px] text-gray-500 whitespace-nowrap">{fmtDatetime(e.occurred_at)}</td>
                    <td className="px-4 py-3">
                      {!e.is_resolved && (
                        <button
                          type="button"
                          onClick={() => resolveError(e)}
                          disabled={resolvingId === e.id}
                          className="flex items-center gap-1 rounded px-2 py-1 text-[11px] font-medium text-green-700 hover:bg-green-50 disabled:opacity-40"
                        >
                          <CheckCircle size={12} />
                          Resolve
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
